import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { sendInstagramAccountCode, sendInstagramAuthMethod, updateConnectionStatus } from 'services/auth/instagramAuthService';
import { getAccessToken } from 'utils/auth/auth';

export type ConnectionStep = 'idle' | 'connecting' | 'code' | 'confirmation' | 'method' | 'success' | 'error';

interface ConnectionState {
  isConnecting: boolean;
  step: ConnectionStep;
  status: string;
  timeLeft: number;
  timerActive: boolean;
  errorMessage: string;
  methods: any[];
  username: string;
}

interface InstagramConnectionContextType {
  connectionState: ConnectionState;
  startConnection: (username: string) => void;
  resumeConnection: (username: string) => void;
  setErrorState: (username: string, errorMessage?: string) => void;
  submitCode: (code: string) => Promise<void>;
  submitMethod: (method: string) => Promise<void>;
  dismissBanner: () => void;
  resetConnection: () => void;
}

const defaultState: ConnectionState = {
  isConnecting: false,
  step: 'idle',
  status: '',
  timeLeft: 300,
  timerActive: false,
  errorMessage: '',
  methods: [],
  username: '',
};

const InstagramConnectionContext = createContext<InstagramConnectionContextType>({
  connectionState: defaultState,
  startConnection: () => {},
  resumeConnection: () => {},
  setErrorState: () => {},
  submitCode: async () => {},
  submitMethod: async () => {},
  dismissBanner: () => {},
  resetConnection: () => {},
});

export const useInstagramConnection = () => useContext(InstagramConnectionContext);

// Helper: update connection status via API (fire-and-forget).
// errorMessage is optional and only meaningful when status === 'connection_issue'.
const updateStatus = async (username: string, status: string, errorMessage?: string) => {
  if (!username) return;
  try {
    await updateConnectionStatus(username, status, errorMessage);
  } catch {
    // Silent — don't block the flow
  }
};

export const InstagramConnectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [connectionState, setConnectionState] = useState<ConnectionState>(defaultState);
  const wsRef = useRef<WebSocket | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Use a ref to always have access to the latest username inside callbacks
  const usernameRef = useRef<string>('');

  // Keep usernameRef in sync
  useEffect(() => {
    usernameRef.current = connectionState.username;
  }, [connectionState.username]);

  // Timer countdown
  useEffect(() => {
    if (connectionState.timerActive && connectionState.timeLeft > 0) {
      timerRef.current = setTimeout(() => {
        setConnectionState(prev => ({ ...prev, timeLeft: prev.timeLeft - 1 }));
      }, 1000);
    } else if (connectionState.timeLeft === 0 && connectionState.timerActive) {
      setConnectionState(prev => ({
        ...prev,
        timerActive: false,
        isConnecting: false,
        step: 'error',
        errorMessage: 'Authentication timed out. Please try again.',
      }));
      if (wsRef.current) wsRef.current.close();
      // Update status on timeout
      updateStatus(usernameRef.current, 'connection_issue', 'Authentication timed out');
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [connectionState.timeLeft, connectionState.timerActive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Auto-reset connection state after success (clears banner everywhere)
  useEffect(() => {
    if (connectionState.step === 'success') {
      const timer = setTimeout(() => {
        setConnectionState(defaultState);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [connectionState.step]);

  // Shared WebSocket message handler setup
  const setupWebSocketHandlers = useCallback((ws: WebSocket) => {
    ws.onmessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      // Handle data.type being either a string or an object with a nested .type
      const messageType = typeof data.type === 'object' && data.type !== null ? data.type.type : data.type;
      const message = data.message || '';

      if (messageType === 'notification') {
        if (message.startsWith('Error logged with') || message.startsWith('Login failed due to incorrect password')) {
          setConnectionState(prev => ({
            ...prev,
            isConnecting: false,
            step: 'error',
            errorMessage: message,
            timerActive: false,
          }));
          wsRef.current?.close();
          updateStatus(usernameRef.current, 'connection_issue', message);
        } else {
          setConnectionState(prev => ({
            ...prev,
            step: 'connecting',
            status: message,
            isConnecting: true,
          }));
        }
      } else if (messageType === 'code_request') {
        setConnectionState(prev => ({
          ...prev,
          step: 'code',
          status: message,
        }));
      } else if (messageType === 'confirmation') {
        setConnectionState(prev => ({
          ...prev,
          step: 'confirmation',
          status: message,
        }));
      } else if (messageType === '2fa_method_request') {
        setConnectionState(prev => ({
          ...prev,
          step: 'method',
          methods: data.methods,
          status: message,
        }));
      } else if (messageType === 'error') {
        setConnectionState(prev => ({
          ...prev,
          isConnecting: false,
          step: 'error',
          errorMessage: message,
          timerActive: false,
        }));
        wsRef.current?.close();
        updateStatus(usernameRef.current, 'connection_issue', message);
      } else if (messageType === 'success') {
        setConnectionState(prev => ({
          ...prev,
          isConnecting: false,
          step: 'success',
          status: message,
          timerActive: false,
        }));
        wsRef.current?.close();
        updateStatus(usernameRef.current, 'active');
      }
    };
  }, []);

  // Opens a new WebSocket connection
  const openWebSocket = useCallback(() => {
    const token = getAccessToken();
    const wsBaseUrl = process.env.REACT_APP_FAMOUS_CLICKER_WEBSOCKET_URL;
    if (!wsBaseUrl || !token) return;

    wsRef.current = new WebSocket(`${wsBaseUrl}?token=${token}&type=user`);
    setupWebSocketHandlers(wsRef.current);
  }, [setupWebSocketHandlers]);

  const startConnection = useCallback((username: string) => {
    // Close any existing WebSocket
    if (wsRef.current) wsRef.current.close();

    usernameRef.current = username;

    setConnectionState({
      isConnecting: true,
      step: 'connecting',
      status: 'Connecting Instagram...',
      timeLeft: 300,
      timerActive: true,
      errorMessage: '',
      methods: [],
      username,
    });

    openWebSocket();
  }, [openWebSocket]);

  // Resume listening on WebSocket for an account already in "connecting" status
  // (e.g. after a page reload). Does NOT start a new auth flow or reset timer.
  const resumeConnection = useCallback((username: string) => {
    // Don't resume if already actively connecting this user
    if (wsRef.current && connectionState.username === username && connectionState.step !== 'idle') return;

    // Close any stale WebSocket
    if (wsRef.current) wsRef.current.close();

    usernameRef.current = username;

    setConnectionState({
      isConnecting: true,
      step: 'connecting',
      status: 'Connecting Instagram...',
      timeLeft: 300,
      timerActive: true,
      errorMessage: '',
      methods: [],
      username,
    });

    openWebSocket();
  }, [openWebSocket, connectionState.username, connectionState.step]);

  // Set the context to error state for an account (e.g. from API connection_issue on page load)
  // Does NOT open a WebSocket — just shows the error banner via ConnectingBanner
  const setErrorState = useCallback((username: string, errorMessage?: string) => {
    // Don't override if already actively tracking a connection
    if (connectionState.step !== 'idle') return;

    usernameRef.current = username;
    setConnectionState({
      isConnecting: false,
      step: 'error',
      status: '',
      timeLeft: 0,
      timerActive: false,
      errorMessage: errorMessage || '',
      methods: [],
      username,
    });
  }, [connectionState.step]);

  const submitCode = useCallback(async (code: string) => {
    try {
      await sendInstagramAccountCode(code);
      setConnectionState(prev => ({
        ...prev,
        step: 'connecting',
        status: 'Code submitted. Authenticating...',
      }));
    } catch {
      setConnectionState(prev => ({
        ...prev,
        status: 'Failed to submit code. Please try again.',
      }));
    }
  }, []);

  const submitMethod = useCallback(async (method: string) => {
    try {
      await sendInstagramAuthMethod(method);
      setConnectionState(prev => ({
        ...prev,
        step: 'connecting',
        status: 'Method selected. Waiting for verification code...',
      }));
    } catch {
      setConnectionState(prev => ({
        ...prev,
        status: 'Failed to submit 2FA method. Please try again.',
      }));
    }
  }, []);

  const dismissBanner = useCallback(() => {
    // Only dismiss the banner visual, keep connection running
    setConnectionState(prev => ({ ...prev, isConnecting: false }));
  }, []);

  const resetConnection = useCallback(() => {
    if (wsRef.current) wsRef.current.close();
    if (timerRef.current) clearTimeout(timerRef.current);
    setConnectionState(defaultState);
  }, []);

  return (
    <InstagramConnectionContext.Provider
      value={{
        connectionState,
        startConnection,
        resumeConnection,
        setErrorState,
        submitCode,
        submitMethod,
        dismissBanner,
        resetConnection,
      }}
    >
      {children}
    </InstagramConnectionContext.Provider>
  );
};
